import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Target, 
  TrendingUp, 
  Monitor, 
  Database, 
  Brain, 
  Lightbulb, 
  FileText, 
  Users, 
  Shield,
  Building2,
  Landmark,
  Factory,
  ArrowRight,
  CheckCircle2,
  Sparkles
} from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: 'easeOut' }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

const services = [
  { icon: Target, title: 'Strategy & Operational Efficiency' },
  { icon: TrendingUp, title: 'Finance Advisory & Performance' },
  { icon: Monitor, title: 'Technology & Digital Transformation' },
  { icon: Database, title: 'Data Readiness & Validation' },
  { icon: Brain, title: 'AI/GenAI/LLM Digital Assets Governance' },
  { icon: Lightbulb, title: 'Ideation & Implementation' },
  { icon: FileText, title: 'Grants & Contracts Support' },
  { icon: Users, title: 'Change Management' },
  { icon: Shield, title: 'Enterprise Risk Management' },
];

const clientTypes = [
  { icon: Factory, title: 'Private Industry', desc: 'Mid-market to enterprise organizations' },
  { icon: Landmark, title: 'Public Sector', desc: 'Agencies, departments, authorities' },
  { icon: Building2, title: 'Quasi-Government', desc: 'Utilities, authorities, higher ed' },
];

const howWeWork = [
  { step: '01', title: 'Assess Reality', desc: 'Process, data, controls, stakeholders, constraints, and real decision paths.' },
  { step: '02', title: 'Design Improvements', desc: 'Fit-for-purpose solutions that are measurable, governed, and implementable.' },
  { step: '03', title: 'Implement & Transfer', desc: 'Playbooks, training, dashboards, governance handoff for sustainable results.' },
];

const HomePage = () => {
  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-grid" data-testid="hero-section">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-radial-top" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <motion.div 
            className="max-w-4xl"
            initial="initial"
            animate="animate"
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="mb-6">
              <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/20 text-cyan-400">
                <Sparkles size={14} className="mr-2" />
                Enterprise-Grade Consulting
              </span>
            </motion.div>
            
            <motion.h1 
              variants={fadeInUp}
              className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.1] mb-6"
            >
              <span className="text-foreground">Performance,</span>
              <br />
              <span className="text-gradient">Governance,</span>
              <br />
              <span className="text-foreground">& Transformation</span>
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="text-xl text-muted-foreground max-w-2xl mb-10 leading-relaxed"
            >
              ANJO Consultants helps organizations improve strategy, finance, operations, and technology—using disciplined governance, clean data, and practical AI controls to deliver measurable results.
            </motion.p>
            
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center justify-center px-8 py-4 rounded-lg font-semibold text-base bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:from-cyan-400 hover:to-blue-400 transition-all duration-300 shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40"
              >
                Book a Discovery Call
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-center px-8 py-4 rounded-lg font-medium text-base border border-border hover:border-cyan-500/50 hover:bg-cyan-500/5 text-foreground transition-all duration-300"
              >
                Request Capability Statement
              </Link>
            </motion.div>
          </motion.div>
        </div>
        
        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <div className="w-6 h-10 rounded-full border-2 border-muted-foreground/30 flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-cyan-400 rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* What We Do Section */}
      <section className="py-32 relative" data-testid="what-we-do-section">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-secondary/30 to-background" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mb-16"
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-foreground mb-6">
              What We <span className="text-gradient">Do</span>
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Organizations fail when execution gets messy: unclear decision rights, weak controls, unreliable data, and technology that adds complexity. We bring structure to that chaos.
            </p>
          </motion.div>

          <motion.div 
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            {services.map((service, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="group p-6 rounded-2xl glass glow-border cursor-pointer hover:bg-secondary/50 transition-all duration-300"
                data-testid={`service-card-${index}`}
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center mb-4 group-hover:from-cyan-500/30 group-hover:to-purple-500/30 transition-all">
                  <service.icon className="text-cyan-400" size={24} />
                </div>
                <h3 className="text-base font-semibold text-foreground group-hover:text-gradient transition-all">
                  {service.title}
                </h3>
              </motion.div>
            ))}
          </motion.div>

          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <Link
              to="/services"
              data-testid="view-services-link"
              className="inline-flex items-center text-cyan-400 font-medium hover:text-cyan-300 transition-colors group"
            >
              View All Services
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Who We Serve Section */}
      <section className="py-32 relative bg-grid-fine" data-testid="who-we-serve-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-foreground mb-4">
              Who We <span className="text-gradient">Serve</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Supporting leaders across private, public, and quasi-government organizations
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {clientTypes.map((client, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group p-8 rounded-2xl glass text-center hover:scale-[1.02] transition-all duration-300"
                data-testid={`client-type-${index}`}
              >
                <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-cyan-500/10 to-purple-500/10 flex items-center justify-center group-hover:from-cyan-500/20 group-hover:to-purple-500/20 transition-all">
                  <client.icon className="text-cyan-400" size={36} />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">{client.title}</h3>
                <p className="text-muted-foreground">{client.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Common Reasons */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-8 rounded-2xl glass glow-border"
          >
            <h3 className="text-xl font-semibold text-foreground mb-6 text-center">Common Reasons Clients Call Us</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                "We need better controls and clearer reporting.",
                "Projects keep slipping and no one trusts the data.",
                "We're adopting AI—now Legal/Risk/Audit is nervous.",
                "We need measurable performance management.",
                "We need staff augmentation that fits governance.",
              ].map((reason, index) => (
                <div key={index} className="flex items-start space-x-3 p-4 rounded-xl hover:bg-secondary/30 transition-colors">
                  <CheckCircle2 className="text-cyan-400 flex-shrink-0 mt-0.5" size={18} />
                  <span className="text-muted-foreground text-sm">&ldquo;{reason}&rdquo;</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How We Work Section */}
      <section className="py-32 relative" data-testid="how-we-work-section">
        <div className="absolute inset-0 bg-radial-center" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl sm:text-5xl font-bold text-foreground mb-16 text-center"
          >
            How We <span className="text-gradient">Work</span>
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {howWeWork.map((item, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
                data-testid={`how-step-${index}`}
              >
                <div className="text-8xl font-bold text-gradient opacity-20 absolute -top-4 -left-2">{item.step}</div>
                <div className="relative pt-16 pl-4">
                  <h3 className="text-2xl font-semibold text-foreground mb-3">{item.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Differentiators Section */}
      <section className="py-32 relative" data-testid="differentiators-section">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 via-purple-500/5 to-cyan-500/5" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl sm:text-5xl font-bold mb-12 text-center"
          >
            What Makes Us <span className="text-gradient">Different</span>
          </motion.h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              'Governance-first delivery that holds up under oversight',
              'Data readiness discipline before analytics or AI/GenAI & Digital Assets',
              'Practical implementation over theory',
              'Outcome accountability with KPIs leadership can defend',
            ].map((item, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center space-x-4 p-6 rounded-xl glass glow-border"
              >
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="text-white" size={24} />
                </div>
                <span className="text-foreground font-medium">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-cyan-500/10 animate-gradient" />
        <div className="absolute inset-0 bg-grid opacity-50" />
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-foreground mb-6">
            Ready to improve performance
            <br />
            <span className="text-gradient">without increasing risk?</span>
          </h2>
          <p className="text-xl text-muted-foreground mb-10">
            Let&apos;s discuss how ANJO Consultants can help your organization.
          </p>
          <Link
            to="/contact"
            data-testid="final-cta"
            className="group inline-flex items-center px-10 py-5 rounded-xl font-semibold text-lg bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:from-cyan-400 hover:to-blue-400 transition-all duration-300 shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40"
          >
            Contact ANJO Consultants
            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
          </Link>
        </motion.div>
      </section>
    </div>
  );
};

export default HomePage;
